import React, { useCallback, useEffect, useState } from 'react';
import '../admin.css';
import { Card, Typography, Form, Input, Select, Button, Table, Space, Modal, message, Tag } from 'antd';
import { UserAddOutlined, DeleteOutlined, CopyOutlined, StopOutlined, MailOutlined } from '@ant-design/icons';
import { API_BASE as apiBase, authHeaders } from '../../config/api';

const { Title } = Typography;

const ManageTeamAccess = () => {
  const [form] = Form.useForm();
  const [inviteForm] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [invites, setInvites] = useState([]);
  
  // Modal states
  const [addMemberModalVisible, setAddMemberModalVisible] = useState(false);
  const [inviteViewerModalVisible, setInviteViewerModalVisible] = useState(false);
  const permissionOptions = [
    { label: 'View Websites', value: 'websites_read' },
    { label: 'Manage Websites', value: 'websites_write' },
    { label: 'View Analytics', value: 'analytics_read' },
    { label: 'Export Analytics', value: 'analytics_export' },
    { label: 'View Events', value: 'events_read' },
    { label: 'Manage Snippets', value: 'snippets_write' },
    { label: 'View Snippets', value: 'snippets_read' },
    { label: 'Manage Team', value: 'team_manage' },
  ];

  const loadTeam = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/my/team`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to load team');
      const items = Array.isArray(body?.items) ? body.items : [];
      setRows(items.map(m => ({ key: m.id, ...m })));
    } catch (e) {
      message.error(e.message || 'Failed to load team');
      setRows([]);
    }
  }, []);

  const loadInvites = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/my/team/invites`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to load invites');
      const items = Array.isArray(body?.items) ? body.items : [];
      setInvites(items.map((x, i) => ({ key: x.id || i, ...x })));
    } catch (e) {
      message.error(e.message || 'Failed to load invites');
      setInvites([]);
    }
  }, []);

  useEffect(() => { loadTeam(); loadInvites(); }, [loadTeam, loadInvites]);

  const onAdd = async () => {
    try {
      const { email, role, permissions, note } = await form.validateFields();
      const res = await fetch(`${apiBase}/my/team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ email, role, permissions: permissions || [], note: note || null })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to add member');
      message.success('Member added');
      form.resetFields();
      setAddMemberModalVisible(false);
      loadTeam();
    } catch (e) { if (e?.message) message.error(e.message); }
  };

  const onInvite = async () => {
    try {
      const { email, permissions, note } = await inviteForm.validateFields();
      const res = await fetch(`${apiBase}/my/team/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ email, role: 'viewer', permissions: permissions || [], note: note || null })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to create invite');
      message.success('Invite created');
      loadInvites();
      inviteForm.resetFields();
      setInviteViewerModalVisible(false);
      const link = `${window.location.origin}/invite?token=${body.token}`;
      Modal.success({
        title: 'Invite Link Ready',
        content: (
          <div>
            <div style={{ wordBreak:'break-all' }}>{link}</div>
            <Button style={{ marginTop: 8 }} icon={<CopyOutlined />} onClick={() => navigator.clipboard.writeText(link)}>Copy Link</Button>
          </div>
        )
      });
    } catch (e) { if (e?.message) message.error(e.message); }
  };

  const revokeInvite = (record) => {
    Modal.confirm({
      title: 'Revoke Invite',
      content: `Revoke invite for ${record.email}?`,
      okText: 'Revoke', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await fetch(`${apiBase}/my/team/invites/${record.id}/revoke`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(body.error || 'Failed to revoke');
          loadInvites();
        } catch (e) { message.error(e.message || 'Failed'); }
      }
    });
  };

  const deleteInvite = (record) => {
    Modal.confirm({
      title: 'Delete Invite',
      content: `Delete invite for ${record.email}?`,
      okText: 'Delete', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await fetch(`${apiBase}/my/team/invites/${record.id}`, {
            method: 'DELETE',
            headers: { ...authHeaders() }
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(body.error || 'Failed to delete');
          loadInvites();
        } catch (e) { message.error(e.message || 'Failed'); }
      }
    });
  };

  const copyInviteLink = async (record) => {
    const link = `${window.location.origin}/invite?token=${record.token}`;
    try { await navigator.clipboard.writeText(link); message.success('Link copied'); } catch { message.warning('Copy failed'); }
  };

  const changeRole = async (record, newRole) => {
    try {
      const res = await fetch(`${apiBase}/my/team/${record.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ role: newRole })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to update role');
      loadTeam();
    } catch (e) { message.error(e.message || 'Failed to update role'); }
  };

  const removeMember = (record) => {
    if (record.role === 'owner') return message.warning('Cannot remove the owner');
    Modal.confirm({
      title: 'Remove Member',
      content: `Remove ${record.email}?`,
      okText: 'Remove', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await fetch(`${apiBase}/my/team/${record.id}`, {
            method: 'DELETE',
            headers: { ...authHeaders() }
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(body.error || 'Failed to remove member');
          loadTeam();
        } catch (e) { message.error(e.message || 'Failed'); }
      }
    });
  };


  // Combine team members and invites into unified data
  const getAllTeamData = () => {
    const activeMembers = rows.map(member => ({
      ...member,
      type: 'member',
      status: 'active',
      actions: 'member'
    }));
    
    const inviteMembers = invites.map(invite => ({
      ...invite,
      type: 'invite',
      actions: 'invite'
    }));
    
    return [...activeMembers, ...inviteMembers];
  };

  const unifiedColumns = [
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Type', dataIndex: 'type', key: 'type', width: 100,
      render: (type) => (
        <Tag color={type === 'member' ? 'green' : 'blue'}>
          {type === 'member' ? 'Member' : 'Invite'}
        </Tag>
      )
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 120,
      render: (status, record) => {
        if (record.type === 'member') {
          return <Tag color="green">Active</Tag>;
        } else {
          return <Tag color={status === 'pending' ? 'blue' : status === 'revoked' ? 'default' : 'green'}>
            {status}
          </Tag>;
        }
      }
    },
    {
      title: 'Role', dataIndex: 'role', key: 'role', width: 150,
      render: (_, record) => {
        if (record.type === 'member') {
          return (
            <Select
              size="small"
              value={record.role}
              onChange={(v) => changeRole(record, v)}
              options={[
                { label:'Viewer', value:'viewer' },
                { label:'Editor', value:'editor' },
                { label:'Owner', value:'owner' },
              ]}
              disabled={record.role==='owner'}
              style={{ minWidth: 120 }}
            />
          );
        } else {
          return <Tag>{record.role}</Tag>;
        }
      }
    },
    {
      title: 'Permissions', dataIndex: 'permissions', key: 'permissions',
      render: (perms=[]) => (
        <Space wrap>
          {(perms || []).map((p) => <Tag key={p}>{p}</Tag>)}
        </Space>
      )
    },
    { title: 'Note', dataIndex: 'note', key: 'note', ellipsis: true },
    {
      title: 'Created', dataIndex: 'created_at', key: 'created_at', width: 180,
      render: (t) => t ? new Date(t).toLocaleDateString() : '-'
    },
    {
      title: 'Actions', key: 'actions', width: 300,
      render: (_, record) => {
        if (record.type === 'member') {
          return (
            <Space>
              <Button size="small" icon={<DeleteOutlined />} danger disabled={record.role==='owner'} onClick={() => removeMember(record)}>Remove</Button>
            </Space>
          );
        } else {
          return (
            <Space>
              <Button size="small" icon={<CopyOutlined />} onClick={() => copyInviteLink(record)}>Copy Link</Button>
              <Button size="small" icon={<StopOutlined />} disabled={record.status !== 'pending'} onClick={() => revokeInvite(record)}>Revoke</Button>
              <Button size="small" icon={<DeleteOutlined />} danger onClick={() => deleteInvite(record)}>Delete</Button>
            </Space>
          );
        }
      }
    }
  ];

  return (
    <div className="abo-content">
      <Title level={2} style={{ marginBottom: 16 }}>Team & Roles</Title>
      
      {/* Team Members section - now includes invites */}
      <Card style={{ borderRadius: 14, marginBottom: 12 }} title="Team Members & Invites">
        <Table 
          columns={unifiedColumns} 
          dataSource={getAllTeamData()} 
          pagination={{ pageSize: 8 }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Action buttons */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
        <Button 
          type="primary" 
          icon={<UserAddOutlined />} 
          onClick={() => setAddMemberModalVisible(true)}
        >
          Add Member
        </Button>
        <Button 
          icon={<MailOutlined />} 
          onClick={() => setInviteViewerModalVisible(true)}
        >
          Invite Viewer
        </Button>
      </div>

      {/* Add Member Modal */}
      <Modal
        title="Add Team Member"
        open={addMemberModalVisible}
        onCancel={() => {
          setAddMemberModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form layout="vertical" form={form} onFinish={onAdd}>
          <Form.Item label="Member Email" name="email" rules={[{ required:true, message:'Email required' }, { type:'email', message:'Valid email required' }]}>
            <Input placeholder="member@example.com" />
          </Form.Item>
          <Form.Item label="Role" name="role" initialValue="viewer">
            <Select options={[{label:'Viewer',value:'viewer'},{label:'Editor',value:'editor'},{label:'Owner',value:'owner'}]} />
          </Form.Item>
          <Form.Item label="Permissions" name="permissions">
            <Select mode="multiple" placeholder="Select permissions" options={permissionOptions} allowClear />
          </Form.Item>
          <Form.Item label="Description / Note" name="note">
            <Input.TextArea rows={3} placeholder="Describe access details" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={() => {
              setAddMemberModalVisible(false);
              form.resetFields();
            }} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">Add Member</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Invite Viewer Modal */}
      <Modal
        title="Invite Viewer"
        open={inviteViewerModalVisible}
        onCancel={() => {
          setInviteViewerModalVisible(false);
          inviteForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form layout="vertical" form={inviteForm} onFinish={onInvite}>
          <Form.Item
            label="Viewer Email"
            name="email"
            rules={[{ required: true, message: 'Please enter an email' }, { type: 'email', message: 'Enter a valid email' }]}
          >
            <Input placeholder="viewer@example.com" prefix={<MailOutlined />} />
          </Form.Item>
          <Form.Item label="Permissions" name="permissions">
            <Select mode="multiple" placeholder="Select permissions" options={permissionOptions} allowClear />
          </Form.Item>
          <Form.Item label="Description / Note" name="note">
            <Input.TextArea rows={3} placeholder="Describe what this viewer can access" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={() => {
              setInviteViewerModalVisible(false);
              inviteForm.resetFields();
            }} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">Send Invite</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageTeamAccess;
